/**
 * UserDetailView Component
 *
 * Display detailed user information with actions
 * Feature: 045-admin-user-tool
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { UserDetailResponse } from '../../graphql/hooks/useUserGraphQL';
import {
  useAdminVerifyUserGraphQL,
  useAdminUnverifyUserGraphQL,
  useAdminSendPasswordResetGraphQL,
  useAdminDeleteUserGraphQL,
  useAdminUpdateUserGraphQL,
} from '../../hooks/graphql/useAdminMutationsGraphQL';
import { useCreateImpersonationLinkGraphQL } from '../../hooks/graphql/useImpersonationGraphQL';
import { useNavigate } from 'react-router-dom';
import { adminUsersUrl } from '../../utils/urls';
import logger from '../../utils/logger';
import { IdLink } from '../IdLink';
import { ImpersonationLinkModal } from '../admin/ImpersonationLinkModal';

interface UserDetailViewProps {
  userDetail: UserDetailResponse;
  onUpdate?: () => void;
}

export function UserDetailView({ userDetail, onUpdate }: UserDetailViewProps) {
  const { user, stats } = userDetail;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: user.username,
    email: user.email,
    displayName: user.displayName
  });

  // Impersonation link modal state (Feature: 075-link-based-impersonation)
  const [impersonationLinkData, setImpersonationLinkData] = useState<{
    url: string;
    pin: string;
    expiresAt: string;
    targetUsername: string;
  } | null>(null);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);

  // GraphQL mutations
  const { verifyUser, loading: verifyLoading } = useAdminVerifyUserGraphQL();
  const { unverifyUser, loading: unverifyLoading } = useAdminUnverifyUserGraphQL();
  const { sendPasswordReset, loading: resetPasswordLoading } = useAdminSendPasswordResetGraphQL();
  const { deleteUser, loading: deleteLoading } = useAdminDeleteUserGraphQL();
  const { updateUser, loading: updateLoading } = useAdminUpdateUserGraphQL();
  const { createLink, loading: createLinkLoading } = useCreateImpersonationLinkGraphQL();

  // Helper to refetch admin user queries (Apollo cache invalidation)
  const refetchAdminQueries = () => {
    apolloClient.refetchQueries({
      include: ['AdminUsers', 'AdminUserStats', 'AdminUserDetail'],
    });
  };

  const handleVerify = async () => {
    if (confirm('Are you sure you want to verify this user?')) {
      try {
        await verifyUser(user.globalId);
        refetchAdminQueries();
        onUpdate?.();
      } catch (error) {
        logger.error('UserDetailView', 'Failed to verify user:', error);
        alert('Failed to verify user');
      }
    }
  };

  const handleUnverify = async () => {
    if (confirm('Are you sure you want to un-verify this user? They will need to verify their email again.')) {
      try {
        await unverifyUser(user.globalId);
        refetchAdminQueries();
        onUpdate?.();
      } catch (error) {
        logger.error('UserDetailView', 'Failed to un-verify user:', error);
        alert('Failed to un-verify user');
      }
    }
  };

  const handleResetPassword = async () => {
    if (confirm('Send password reset email to this user?')) {
      try {
        const result = await sendPasswordReset(user.globalId);
        if (result?.success) {
          alert('Password reset email sent successfully');
        } else {
          alert('Password reset initiated but email may not have been sent');
        }
      } catch (error) {
        logger.error('UserDetailView', 'Failed to reset password:', error);
        alert('Failed to reset password');
      }
    }
  };

  const handleDelete = async () => {
    if (
      confirm(
        `Are you sure you want to delete user "${user.username}"? This action cannot be undone and will delete all their content (photos, collections, critiques).`
      )
    ) {
      try {
        await deleteUser(user.globalId);
        refetchAdminQueries();
        alert('User deleted successfully');
        navigate(adminUsersUrl());
      } catch (error: any) {
        logger.error('UserDetailView', 'Failed to delete user:', error);
        alert(error.message || 'Failed to delete user');
      }
    }
  };

  const handleSave = async () => {
    try {
      await updateUser({
        id: user.globalId,
        username: editForm.username,
        email: editForm.email,
        displayName: editForm.displayName
      });
      refetchAdminQueries();
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      logger.error('UserDetailView', 'Failed to update user:', error);
      alert(error.message || 'Failed to update user');
    }
  };

  const handleCancel = () => {
    setEditForm({
      username: user.username,
      email: user.email,
      displayName: user.displayName
    });
    setIsEditing(false);
  };

  /**
   * Handle creating an impersonation link (Feature: 075-link-based-impersonation)
   * This generates a URL + PIN that can be shared with the user
   * for login without requiring admin session on their machine.
   */
  const handleCreateImpersonationLink = async () => {
    try {
      const result = await createLink(user.globalId);

      if (result) {
        setImpersonationLinkData(result);
        setShowImpersonationModal(true);
      }
    } catch (error: any) {
      logger.error('UserDetailView', 'Failed to create impersonation link:', error);
      alert(error.message || 'Failed to create impersonation link');
    }
  };

  const handleCloseImpersonationModal = () => {
    setShowImpersonationModal(false);
    setImpersonationLinkData(null);
  };

  return (
    <div className="space-y-6">
      {/* User Information */}
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{user.displayName}</h2>
            <div className="text-sm text-muted mt-1 flex items-center gap-2">
              <span>Global ID:</span>
              <IdLink globalId={user.globalId} typeName="User" className="text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            {user.emailVerified ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-500">
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-500/20 text-yellow-500">
                Unverified
              </span>
            )}
            {user.isTestUser && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-500/20 text-purple-500">
                Test User
              </span>
            )}
            {user.isAdmin && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/20 text-blue-500">
                Admin
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isEditing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-muted mb-1">Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted mb-1">Display Name</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-muted mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </>
          ) : (
            <>
              <DetailField label="Username" value={user.username} />
              <DetailField label="Display Name" value={user.displayName} />
              <DetailField label="Email" value={user.email} />
              <DetailField label="Slug" value={user.slug} />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <DetailField
            label="Created"
            value={new Date(user.createdAt).toLocaleString()}
          />
          <DetailField
            label="Updated"
            value={new Date(user.updatedAt).toLocaleString()}
          />
          <DetailField
            label="Last Login"
            value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
          />
          {/* Feature: 891-invite-attribution */}
          <DetailField
            label="Invited By"
            value={user.invitedBy
              ? `${user.invitedBy.displayName || user.invitedBy.username} (@${user.invitedBy.username})`
              : 'Not invited (direct signup)'
            }
          />
        </div>

        {/* Edit Actions */}
        <div className="mt-6 flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={updateLoading}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {updateLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Content Statistics</h3>
        <div className="grid grid-cols-3 gap-4">
          <StatBox label="Photos" value={stats.photoCount} />
          <StatBox label="Collections" value={stats.collectionCount} />
          <StatBox label="Critiques" value={stats.critiqueCount} />
        </div>
      </div>

      {/* Actions */}
      <div className="bg-surface-1 rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Admin Actions</h3>
        <div className="flex flex-wrap gap-3">
          {!user.emailVerified && (
            <button
              onClick={handleVerify}
              disabled={verifyLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {verifyLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          )}
          {user.emailVerified && (
            <button
              onClick={handleUnverify}
              disabled={unverifyLoading}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 transition-colors"
            >
              {unverifyLoading ? 'Unverifying...' : 'Un-verify Email'}
            </button>
          )}
          <button
            onClick={handleResetPassword}
            disabled={resetPasswordLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {resetPasswordLoading ? 'Sending...' : 'Reset Password'}
          </button>
          <button
            onClick={handleCreateImpersonationLink}
            disabled={createLinkLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
            title="Generate a link + PIN for the user to login without your admin session"
          >
            {createLinkLoading ? 'Creating...' : 'Login As User'}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteLoading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {deleteLoading ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </div>

      {/* Impersonation Link Modal (Feature: 075-link-based-impersonation) */}
      <ImpersonationLinkModal
        open={showImpersonationModal}
        onClose={handleCloseImpersonationModal}
        data={impersonationLinkData}
      />
    </div>
  );
}

interface DetailFieldProps {
  label: string;
  value: string;
}

function DetailField({ label, value }: DetailFieldProps) {
  return (
    <div>
      <dt className="text-sm font-medium text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-foreground">{value}</dd>
    </div>
  );
}

interface StatBoxProps {
  label: string;
  value: number;
}

function StatBox({ label, value }: StatBoxProps) {
  return (
    <div className="bg-surface-2 rounded-lg p-4 border border-border text-center">
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-sm text-muted mt-1">{label}</div>
    </div>
  );
}
